// i18n area 'common' (task #127): sections nav, mode, language, notFound, scopeBrowser, search, stub, home, notes, common, context, crud.
// Split out of the old monolithic dictionaries.ts by scripts/split-dictionaries.mjs;
// since the split, these files are the SOURCE — add and edit keys here. The script
// only re-runs to re-split a merged monolithic dictionaries.ts (see its header).

import type { PluralForms } from '../plural'

// Helper locale al file, identico a plural() in ../plural: un import RUNTIME
// da una sezione romperebbe scripts/i18n-status.mjs, che carica questi file
// con node (type stripping, niente resolver TS) — l'import type si cancella,
// uno runtime no. Il tipo resta l'unico e vero contratto.
const plural = (forms: PluralForms): PluralForms => forms

export const enUS = {
  nav: {
    home: 'Home',
    vms: 'Virtual machines',
    templates: 'Templates',
    importVms: 'Import VMs',
    containers: 'Containers',
    containersApps: 'Apps',
    containersPods: 'Pods',
    containersCt: 'CT',
    multiscsi: 'TVStor MultiSCSI',
    containersOciImages: 'OCI images',
    containersOciRegistries: 'OCI registries',
    containersCtTemplates: 'CT templates',
    networkServices: 'Network services',
    firewall: 'Firewall',
    checkpoints: 'Checkpoints',
    backup: 'Backup',
    monitoring: 'Monitoring',
    cluster: 'Cluster',
    clusterSettings: 'Global settings',
    clusterNodes: 'Nodes',
    clusterDevices: 'USB & PCIe resources',
    clusterFederation: 'Federation',
    network: 'Network',
    overlayNetworks: 'Overlay networks',
    fabric: 'Fabric',
    ipam: 'IPAM',
    storage: 'Storage',
    data: 'Data',
    storages: 'Storage',
    hci: 'Hyperconvergence',
    ceph: 'Ceph',
    system: 'System',
    tasks: 'Tasks',
    events: 'Events',
    audit: 'Audit',
    diagnostics: 'Diagnostics',
    notifications: 'Notifications',
    proximity: 'TVirt Proximity',
    users: 'Users & groups',
    groups: 'Groups',
    mfa: 'MFA',
    realms: 'Realms & SSO',
    rbac: 'Roles & permissions',
    credentials: 'Credentials',
    scopes: 'Scopes',
    tags: 'Tags',
    explorer: 'Explore',
    licensing: 'Licensing',
    information: 'Information'
  },
  mode: {
    light: 'Light',
    dark: 'Dark',
    system: 'System',
    tooltip: 'Theme'
  },
  language: {
    tooltip: 'Language'
  },

  // Intestazioni delle sottosezioni per continente del selettore lingue
  // (ratifica Luca 2026-08-07): una chiave per LangRegion di dictionaries.ts.
  langRegion: {
    europe: 'Europe',
    northAmerica: 'North America',
    southAmerica: 'South America',
    asia: 'Asia'
  },
  notFound: {
    title: 'Page not found',
    body: 'This address does not match any TVirt page — it may have moved with an update, or the link is stale.',
    backHome: 'Back to Home'
  },
  scopeBrowser: {
    title: 'Scopes',
    all: 'Whole cluster',
    noChildren: 'No sub-scopes here',
    recursive: 'Include descendants',
    collapse: 'Collapse the scope panel',
    openPicker: 'Browse scopes',
    up: 'Up one level',
    countTooltip: '{direct} in this scope · {total} including descendants'
  },
  search: {
    tooltip: 'Search (⌘K)',
    placeholder: 'Search VMs, networks, storage…',
    empty: 'No matches',

    // Command palette round (#221, ratified 2026-08-06): group headers, the
    // kinds living outside the explorer census, the key legend and the verbs.
    groups: {
      recent: 'Recent',
      shortcuts: 'Shortcuts',
      actions: 'Actions',
      goTo: 'Go to'
    },
    kind: {
      catalog: 'Catalog',
      scope: 'Scope',
      node: 'Node'
    },
    footer: {
      navigate: 'navigate',
      open: 'open',
      close: 'close'
    },
    actions: {
      createVm: 'Create VM…',
      uploadIso: 'Upload ISO…',
      clusterSettings: 'Cluster settings',
      consoleOf: 'Go to the console of {name}'
    }
  },
  stub: {
    containersSummary: 'Applications and system containers (CT), with their OCI images, registries and templates — managed alongside your VMs.',
    sectionSummary_vms: 'Virtual machines and their templates — lifecycle, snapshots, migration and consoles.',
    sectionSummary_storages: 'The storage pools where disks, images and VM metadata live.',
    sectionSummary_catalogs: 'The home of the cluster\'s file artifacts: ISO images, CT and VM templates and OCI archives, organized in catalogs.',
    sectionSummary_overlay: 'VXLAN overlay networks between the cluster nodes.',
    sectionSummary_fabric: 'The physical network inventory — switches, ports, LAGGs and logical networks.',
    sectionSummary_ipam: 'Addressing derived from fabric and overlay networks: who holds each address.',
    sectionSummary_services: 'DHCP and DNS servers rendered from the network registry.',
    sectionSummary_ceph: 'Managed Ceph clusters on the TVirt nodes.',
    sectionSummary_hci: 'Hyperconverged storage technologies on the TVirt nodes.',
    sectionSummary_cluster: 'Nodes, membership and cluster-wide settings.',
    containers: 'System containers are on the roadmap — this section will host them.',
    containersApps: 'Containerized applications are on the roadmap — this area will host them.',
    containersPods: 'Pods group containers that share network and volumes — the instances are the Apps.',
    containersCt: 'System containers (CT) are on the roadmap — this area will host them.',
    multiscsi: 'TVStor MultiSCSI is on the roadmap — this area will host it.',
    clusterFederation: 'Federation between TVirt clusters is on the roadmap — this area will host it.',
    containersOciImages: 'OCI images are on the roadmap — this area will host them.',
    containersOciRegistries: 'OCI registries are on the roadmap — this area will host them.',
    containersCtTemplates: 'CT templates are on the roadmap — this area will host them.',
    importVms: 'ESXi and Proxmox importers will land here: guest conversion and bulk migration into TVirt.',
    backup: 'Integrated backup arrives in 6.2: deduplicated content-addressed repositories, CBT incrementals, immutability windows and repo-to-repo replication.',
    monitoring: 'Cluster-wide contention dashboards (cpu.ready, PSI pressure, built-in alerts) are coming — today the same metrics live in each VM\'s details.'
  },
  home: {
    welcome: 'You\'re logged in as: {name}',
    subtitle: 'Here is your TVirt cluster at a glance.',
    loadError: 'Could not load the overview — is the TVirt backend reachable?',
    health: {
      ok: 'All good',
      warning: 'Attention needed',
      critical: 'Critical issues'
    },
    reason: {
      'node-down': '{subject} is down',
      'node-stale': '{subject} not seen recently',
      'node-maintenance': '{subject} in maintenance',
      'tasks-failed': plural({
        one: '{n} failed task in the last 24h',
        other: '{n} failed tasks in the last 24h'
      }),
      'storage-degraded': 'storage {subject} degraded',
      'storage-error': 'storage {subject} in error',
      'ceph-warning': 'Ceph {subject}: HEALTH_WARN',
      'ceph-error': 'Ceph {subject}: HEALTH_ERR',
      'store-unreachable': 'cluster store unreachable',
      'license-absent': 'no license — new VMs cannot start',
      'license-expired': 'license expired — new VMs cannot start',
      'license-exceeded': 'license limit exceeded — new VMs cannot start',
      'failover-reserve-eroded': 'failover reserve nearly consumed',
      'failover-reserve-exhausted': 'failover reserve exhausted — new VMs cannot start',

      'oci-cache-nearly-full': 'OCI cache nearly full on {subject} — digests are evicted by hand',
      'oci-cache-full': 'OCI cache full on {subject} — no pull will succeed until digests are evicted',
      'config-replica-empty': 'config replica EMPTY on {subject} — the node has no cluster config until a replication pass succeeds',
      'config-replica-stalled': 'config replication stalled on {subject} — the local copy lags behind the cluster config'
    },
    staleChip: 'Data not up to date',
    staleAsOf: 'last replicated {time}',
    zone: {
      nodes: 'Nodes',
      failoverReserve: 'Failover reserve',
      capacity: 'Capacity',
      workload: 'Workload',
      protection: 'Protection',
      activity: 'Recent activity'
    },

    // The failover reserve zone (admission control N+1, task #133b).
    reserve: {
      state: {
        ok: 'Reserve intact',
        eroded: 'Reserve nearly consumed',
        exhausted: 'Reserve exhausted',
        unknown: 'Reserve unknown'
      },
      tolerate: plural({
        one: 'tolerates losing {n} node',
        other: 'tolerates losing {n} nodes'
      }),
      usedOf: '{used} / {allowed} GiB in use',
      keepFree: '{n} GiB kept free for failover',
      expendable: 'of which {n} expendable in service',
      headroom: '{n} GiB may still be started',
      short: 'the reserve is short by {n} GiB — new VMs cannot start',

      // Huge pages per size (ratified 2026-08-04) — the label-colon form on
      // purpose: no language has to decline after a variable number.
      hp: {
        usedOf: '{size} pages — in use: {used} / {allowed}',
        keepFree: 'kept free for failover: {keep}',
        headroom: 'may still be started: {n}',
        short: 'missing from the reserve: {n} — new huge-page VMs cannot start'
      },
      worstCase: 'worst case: {nodes}',
      unavailable: '{node}: {reason}',
      unknown: 'No node has reported its memory yet — the reserve cannot be computed, and no start is refused.',
      reason: {
        attestedDown: 'attested down',
        maintenance: 'in maintenance',
        unreachable: 'unreachable',
        capacityUnknown: 'memory not reported'
      }
    },
    node: {
      cpu: '{n} CPU',
      cpuTopology: '{cores} cores / {threads} threads',
      memUsedOf: '{used} / {total} GiB',
      activeVms: plural({
        one: '{n} active VM',
        other: '{n} active VMs'
      }),
      activeVmsUnknown: 'active VMs unknown',
      lastSeen: 'seen {time}',
      neverSeen: 'never seen',
      down: 'Down',
      maintenance: 'Maintenance',
      stale: 'Unreachable'
    },
    capacity: {
      usedOf: '{used} / {total} GiB',
      provisioned: '{n} GiB provisioned',
      reserve: 'reserve {n} GiB',
      unobserved: 'capacity not observed yet',
      empty: 'No storage yet'
    },
    workload: {
      vms: 'Virtual machines',
      apps: 'Apps',
      services: 'Network services',
      empty: 'None yet',
      phasesUnknown: 'phase breakdown unavailable'
    },
    protection: {
      none: 'No backup destination configured yet — protect your configuration.',
      configure: 'Configure checkpoints',
      noRuns: 'No checkpoint has been taken yet.',
      last: 'Last checkpoint',
      onTarget: 'on {target}',
      viewAll: 'Checkpoints'
    },
    activity: {
      empty: 'No recent activity',
      failed24: plural({
        one: '{n} failed in 24h',
        other: '{n} failed in 24h'
      }),
      viewAll: 'View all tasks'
    }
  },
  notes: {
    title: 'Notes',
    tabEdit: 'Edit',
    tabPreview: 'Preview',
    placeholder: 'Free-form operator notes — Markdown is rendered in the preview',
    empty: 'No notes',
    tooLarge: 'Notes exceed the 64 KiB limit',
    saveError: 'Could not save the notes'
  },
  common: {

    // Round «errori onesti» (2026-08-03): una risposta 200 che questo client
    // non sa leggere NON è una rete giù — accusare il backend manda
    // l'operatore a guardare un servizio che funziona (bug hunt #157, U1).
    contractError: 'The backend response contains a value this interface does not know: {field}={value} — update the web interface to the backend version.',
    contractErrorItems: '{count} objects in this list cannot be read by this interface ({field}={value}) and are not shown.',
    staleHint: "The store is unreachable — showing this node's config replica; fleet state is unknown.",
    staleStateAge: 'state as of {time}',
    staleStateNever: "state never received from the store: this node's own observations only",
    staleStateTooltip: "The cluster store is unreachable: the observed state (phase, node, agent) comes from this node's state replica and dates from the last successful read of the store — it may be stale.",
    noPermission: 'You do not have permission for this action',

    // Un budget di attesa scaduto NON è un esito (Luca 2026-08-06): il
    // cronometro del client misura la pazienza del client, non lo stato del
    // server — l'avviso dice «ancora in corso», mai «non riuscita».
    actionStillRunning: 'The action is still running — its outcome is not yet known',
    staleWrite: 'Someone else changed this object while you were editing it — nothing was written. Reload and repeat your change on the fresh state.',
    staleReload: 'Reload',

    // Parity R1 (Q10 a, 2026-11-03): the «Anteprima» of a create/edit dialog —
    // the server runs the full admission of the write and rolls it back; the
    // panel shows the object that WOULD be born or changed.
    preview: 'Preview',
    previewTitle: 'Preview of the result',
    previewNothingWritten: 'Simulated on the server — nothing was written.',
    previewChanged: 'Highlighted: the values that would change.'
  },
  context: {
    copySelection: 'Copy selected text'
  },
  crud: {
    create: 'Create',
    viewDetails: 'View details',
    viewOrEdit: 'View or edit',
    selected: 'selected',
    viewUsingVms: 'See the VMs using this resource',
    clearSelection: 'Clear',
    deleteSelected: 'Delete selected',
    bulkConfirmBody: plural({
      one: 'Delete the {count} selected item? If protected it will be reported and kept.',
      other: 'Delete the {count} selected items? Protected ones will be reported and kept.'
    }),
    bulkDeleted: 'deleted',
    bulkRefused: 'refused',

    // Round #156 (dialog standard): the generic metadata rows every details
    // dialog closes with, and the shared immutability hints for the lock icon.
    fieldId: 'ID',
    createdAt: 'Created at',
    updatedAt: 'Updated at',
    immutableField: 'Fixed at creation — the contract does not allow changing it',
    immutableScope: 'The scope is assigned at creation; some resources move with a dedicated action',
    observedField: 'Observed state — reported by the platform, not editable'
  }
}

export const itIT: typeof enUS = {
  nav: {
    home: 'Home',
    vms: 'Macchine virtuali',
    templates: 'Template',
    importVms: 'Importa VM',
    containers: 'Container',
    containersApps: 'App',
    containersPods: 'Pod',
    containersCt: 'CT',
    multiscsi: 'TVStor MultiSCSI',
    containersOciImages: 'Immagini OCI',
    containersOciRegistries: 'Registry OCI',
    containersCtTemplates: 'Template CT',
    networkServices: 'Servizi di rete',
    firewall: 'Firewall',
    checkpoints: 'Checkpoint',
    backup: 'Backup',
    monitoring: 'Monitoraggio',
    cluster: 'Cluster',
    clusterSettings: 'Impostazioni globali',
    clusterNodes: 'Nodi',
    clusterDevices: 'Risorse USB e PCIe',
    clusterFederation: 'Federazione',
    network: 'Rete',
    overlayNetworks: 'Reti overlay',
    fabric: 'Fabric',
    ipam: 'IPAM',
    storage: 'Storage',
    data: 'Dati',
    storages: 'Storage',
    hci: 'Iperconvergenza',
    ceph: 'Ceph',
    system: 'Sistema',
    tasks: 'Task',
    events: 'Eventi',
    audit: 'Audit',
    diagnostics: 'Diagnostica',
    notifications: 'Notifiche',
    proximity: 'TVirt Proximity',
    users: 'Utenti e gruppi',
    groups: 'Gruppi',
    mfa: 'MFA',
    realms: 'Realm e SSO',
    rbac: 'Ruoli e permessi',
    credentials: 'Credenziali',
    scopes: 'Scope',
    tags: 'Tag',
    explorer: 'Esplora',
    licensing: 'Licenza',
    information: 'Informazioni'
  },
  mode: {
    light: 'Chiaro',
    dark: 'Scuro',
    system: 'Sistema',
    tooltip: 'Tema'
  },
  language: {
    tooltip: 'Lingua'
  },
  langRegion: {
    europe: 'Europa',
    northAmerica: 'Nord America',
    southAmerica: 'Sud America',
    asia: 'Asia'
  },
  notFound: {
    title: 'Pagina non trovata',
    body: "Questo indirizzo non corrisponde a nessuna pagina di TVirt — può essersi spostato con un aggiornamento, o il link è vecchio.",
    backHome: 'Torna alla Home'
  },
  scopeBrowser: {
    title: 'Scope',
    all: 'Tutto il cluster',
    noChildren: 'Nessun sotto-scope qui',
    recursive: 'Includi discendenti',
    collapse: 'Comprimi il pannello scope',
    openPicker: 'Esplora gli scope',
    up: 'Sali di livello',
    countTooltip: '{direct} in questo scope · {total} con i discendenti'
  },
  search: {
    tooltip: 'Cerca (⌘K)',
    placeholder: 'Cerca VM, reti, storage…',
    empty: 'Nessun risultato',

    // Round command palette (#221, ratifica 2026-08-06): intestazioni dei
    // gruppi, i tipi fuori dal censimento explorer, il vocabolario tasti e i
    // verbi del gruppo Azioni.
    groups: {
      recent: 'Recenti',
      shortcuts: 'Scorciatoie',
      actions: 'Azioni',
      goTo: 'Vai a'
    },
    kind: {
      catalog: 'Catalogo',
      scope: 'Scope',
      node: 'Nodo'
    },
    footer: {
      navigate: 'naviga',
      open: 'apri',
      close: 'chiude'
    },
    actions: {
      createVm: 'Crea VM…',
      uploadIso: 'Carica ISO…',
      clusterSettings: 'Impostazioni cluster',
      consoleOf: 'Vai alla console di {name}'
    }
  },
  stub: {
    containersSummary: 'Applicazioni e container di sistema (CT), con le loro immagini OCI, i registry e i template — gestiti accanto alle tue VM.',
    sectionSummary_vms: 'Macchine virtuali e i loro template — ciclo di vita, snapshot, migrazione e console.',
    sectionSummary_storages: 'Gli storage dove vivono dischi, immagini e metadati delle VM.',
    sectionSummary_catalogs: 'La casa degli artefatti file del cluster: immagini ISO, template CT e VM e archivi OCI, organizzati in cataloghi.',
    sectionSummary_overlay: 'Reti overlay VXLAN tra i nodi del cluster.',
    sectionSummary_fabric: "L'inventario della rete fisica — switch, porte, LAGG e reti logiche.",
    sectionSummary_ipam: "L'indirizzamento derivato dalle reti fabric e overlay: chi tiene ogni indirizzo.",
    sectionSummary_services: 'Server DHCP e DNS derivati dal registro di rete.',
    sectionSummary_ceph: 'Cluster Ceph gestiti sui nodi TVirt.',
    sectionSummary_hci: 'Le tecnologie storage iperconvergenti sui nodi TVirt.',
    sectionSummary_cluster: 'Nodi, appartenenza e impostazioni di cluster.',
    containers: 'I container di sistema sono in roadmap — questa sezione li ospiterà.',
    containersApps: 'Le applicazioni containerizzate sono in roadmap — questa area le ospiterà.',
    containersPods: 'I Pod raggruppano container che condividono rete e volumi — le istanze sono le App.',
    containersCt: 'I container di sistema (CT) sono in roadmap — questa area li ospiterà.',
    multiscsi: 'TVStor MultiSCSI è in roadmap — questa area lo ospiterà.',
    clusterFederation: 'La federazione tra cluster TVirt è in roadmap — questa area la ospiterà.',
    containersOciImages: 'Le immagini OCI sono in roadmap — questa area le ospiterà.',
    containersOciRegistries: 'I registry OCI sono in roadmap — questa area li ospiterà.',
    containersCtTemplates: 'I template CT sono in roadmap — questa area li ospiterà.',
    importVms: 'Gli importer ESXi e Proxmox arriveranno qui: conversione dei guest e migrazione in massa dentro TVirt.',
    backup: 'Il backup integrato arriva con la 6.2: repository content-addressed deduplicati, incrementali CBT, finestre di immutabilità e replica repo-to-repo.',
    monitoring: 'Le dashboard di contesa cluster-wide (cpu.ready, pressione PSI, alert integrati) sono in arrivo — oggi le stesse metriche vivono nei dettagli di ogni VM.'
  },
  home: {
    welcome: 'Sei entrato con l\'utenza: {name}',
    subtitle: 'Ecco il tuo cluster TVirt a colpo d\'occhio.',
    loadError: 'Impossibile caricare la panoramica — il backend TVirt è raggiungibile?',
    health: {
      ok: 'Tutto regolare',
      warning: 'Attenzione',
      critical: 'Problemi critici'
    },
    reason: {
      'node-down': '{subject} è spento',
      'node-stale': '{subject} non si fa sentire da un po\'',
      'node-maintenance': '{subject} in manutenzione',
      'tasks-failed': {
        one: '{n} task fallito nelle ultime 24h',
        many: '{n} task falliti nelle ultime 24h',
        other: '{n} task falliti nelle ultime 24h'
      },
      'storage-degraded': 'storage {subject} degradato',
      'storage-error': 'storage {subject} in errore',
      'ceph-warning': 'Ceph {subject}: HEALTH_WARN',
      'ceph-error': 'Ceph {subject}: HEALTH_ERR',
      'store-unreachable': 'store del cluster irraggiungibile',
      'license-absent': 'nessuna licenza — le nuove VM non possono partire',
      'license-expired': 'licenza scaduta — le nuove VM non possono partire',
      'license-exceeded': 'limite di licenza superato — le nuove VM non possono partire',
      'failover-reserve-eroded': 'riserva di failover quasi consumata',
      'failover-reserve-exhausted': 'riserva di failover esaurita — le nuove VM non possono partire',

      'oci-cache-nearly-full': 'cache OCI quasi piena su {subject} — i digest si evizionano a mano',
      'oci-cache-full': 'cache OCI piena su {subject} — nessun pull riuscirà finché non evizioni dei digest',
      'config-replica-empty': 'replica di configurazione VUOTA su {subject} — il nodo non ha alcuna config del cluster finché una replica non riesce',
      'config-replica-stalled': 'replica di configurazione ferma su {subject} — la copia locale resta indietro rispetto alla config del cluster'
    },
    staleChip: 'Dati non aggiornati',
    staleAsOf: 'ultima replica {time}',
    zone: {
      nodes: 'Nodi',
      failoverReserve: 'Riserva di failover',
      capacity: 'Capacità',
      workload: 'Carico di lavoro',
      protection: 'Protezione',
      activity: 'Attività recente'
    },

    // La zona riserva di failover (admission control N+1, task #133b).
    reserve: {
      state: {
        ok: 'Riserva integra',
        eroded: 'Riserva quasi consumata',
        exhausted: 'Riserva esaurita',
        unknown: 'Riserva sconosciuta'
      },
      tolerate: {
        one: 'tollera la perdita di {n} nodo',
        many: 'tollera la perdita di {n} nodi',
        other: 'tollera la perdita di {n} nodi'
      },
      usedOf: '{used} / {allowed} GiB in uso',
      keepFree: '{n} GiB tenuti liberi per il failover',
      expendable: 'di cui {n} sacrificabili in esercizio',
      headroom: 'si possono ancora avviare {n} GiB',
      short: 'alla riserva mancano {n} GiB — le nuove VM non possono partire',

      // Pagine enormi per taglia (ratifica 2026-08-04) — forma «etichetta:
      // {n}» apposta: nessuna lingua deve declinare dopo un numero variabile.
      hp: {
        usedOf: 'Pagine da {size} — in uso: {used} / {allowed}',
        keepFree: 'tenute libere per il failover: {keep}',
        headroom: 'ancora avviabili: {n}',
        short: 'mancanti alla riserva: {n} — le nuove VM con pagine enormi non possono partire'
      },
      worstCase: 'caso peggiore: {nodes}',
      unavailable: '{node}: {reason}',
      unknown: 'Nessun nodo ha ancora dichiarato la memoria — la riserva non è calcolabile e nessun avvio viene rifiutato.',
      reason: {
        attestedDown: 'attestato spento',
        maintenance: 'in manutenzione',
        unreachable: 'irraggiungibile',
        capacityUnknown: 'memoria non dichiarata'
      }
    },
    node: {
      cpu: '{n} CPU',
      cpuTopology: '{cores} core / {threads} thread',
      memUsedOf: '{used} / {total} GiB',
      activeVms: {
        one: '{n} VM attiva',
        many: '{n} VM attive',
        other: '{n} VM attive'
      },
      activeVmsUnknown: 'VM attive sconosciute',
      lastSeen: 'visto {time}',
      neverSeen: 'mai visto',
      down: 'Spento',
      maintenance: 'Manutenzione',
      stale: 'Irraggiungibile'
    },
    capacity: {
      usedOf: '{used} / {total} GiB',
      provisioned: '{n} GiB provisionati',
      reserve: 'riserva {n} GiB',
      unobserved: 'capacità non ancora osservata',
      empty: 'Nessuno storage ancora'
    },
    workload: {
      vms: 'Macchine virtuali',
      apps: 'App',
      services: 'Servizi di rete',
      empty: 'Ancora nessuno',
      phasesUnknown: 'ripartizione per fase non disponibile'
    },
    protection: {
      none: 'Nessuna destinazione di backup configurata — proteggi la tua configurazione.',
      configure: 'Configura i checkpoint',
      noRuns: 'Nessun checkpoint è ancora stato creato.',
      last: 'Ultimo checkpoint',
      onTarget: 'su {target}',
      viewAll: 'Checkpoint'
    },
    activity: {
      empty: 'Nessuna attività recente',
      failed24: {
        one: '{n} fallito in 24h',
        many: '{n} falliti in 24h',
        other: '{n} falliti in 24h'
      },
      viewAll: 'Vedi tutti i task'
    }
  },
  notes: {
    title: 'Note',
    tabEdit: 'Modifica',
    tabPreview: 'Anteprima',
    placeholder: 'Note libere dell\'operatore — il Markdown viene renderizzato nell\'anteprima',
    empty: 'Nessuna nota',
    tooLarge: 'Le note superano il limite di 64 KiB',
    saveError: 'Salvataggio note non riuscito'
  },
  common: {

    // Round «errori onesti» (2026-08-03): una risposta 200 che questo client
    // non sa leggere NON è una rete giù — accusare il backend manda
    // l'operatore a guardare un servizio che funziona (bug hunt #157, U1).
    contractError: 'La risposta del backend contiene un valore che questa interfaccia non conosce: {field}={value} — aggiorna la webui alla versione del backend.',
    contractErrorItems: '{count} oggetti di questo elenco non sono leggibili da questa interfaccia ({field}={value}) e non vengono mostrati.',
    staleHint: 'Lo store non è raggiungibile — questa è la replica di config di questo nodo; lo stato della flotta è sconosciuto.',
    staleStateAge: 'stato aggiornato {time}',
    staleStateNever: 'stato mai ricevuto dallo store: vedi solo le osservazioni di questo nodo',
    staleStateTooltip: "Lo store del cluster non è raggiungibile: lo stato osservato (fase, nodo, agente) viene dalla replica di stato di questo nodo e risale all'ultima lettura riuscita dallo store — può essere stantio.",
    noPermission: 'Non hai il permesso per questa azione',

    // Un budget di attesa scaduto NON è un esito (Luca 2026-08-06): il
    // cronometro del client misura la pazienza del client, non lo stato del
    // server — l'avviso dice «ancora in corso», mai «non riuscita».
    actionStillRunning: 'L\'azione è ancora in corso: l\'esito non è ancora noto',
    staleWrite: 'Qualcun altro ha modificato questo oggetto mentre lo stavi modificando — non è stato scritto nulla. Ricarica e ripeti la modifica sullo stato aggiornato.',
    staleReload: 'Ricarica',

    // Parità R1 (Q10 a, 2026-11-03): l'«Anteprima» di un dialog di crea/modifica —
    // il server esegue l'ammissione completa della scrittura e la annulla; il
    // pannello mostra l'oggetto che NASCEREBBE o CAMBIEREBBE.
    preview: 'Anteprima',
    previewTitle: 'Anteprima del risultato',
    previewNothingWritten: 'Simulata sul server — non è stato scritto nulla.',
    previewChanged: 'In evidenza i valori che cambierebbero.'
  },
  context: {
    copySelection: 'Copia testo selezionato'
  },
  crud: {
    create: 'Crea',
    viewDetails: 'Vedi dettagli',
    viewOrEdit: 'Vedi o modifica',
    selected: 'selezionati',
    viewUsingVms: 'Vedi VM che usano la risorsa',
    clearSelection: 'Deseleziona',
    deleteSelected: 'Elimina selezionati',
    bulkConfirmBody: {
      one: 'Eliminare {count} elemento selezionato? Se protetto verrà segnalato e mantenuto.',
      many: 'Eliminare i {count} elementi selezionati? Quelli protetti verranno segnalati e mantenuti.',
      other: 'Eliminare i {count} elementi selezionati? Quelli protetti verranno segnalati e mantenuti.'
    },
    bulkDeleted: 'eliminati',
    bulkRefused: 'rifiutati',
    fieldId: 'ID',
    createdAt: 'Creato il',
    updatedAt: 'Aggiornato il',
    immutableField: 'Fissato alla creazione — il contract non ne consente la modifica',
    immutableScope: 'Lo scope si assegna alla creazione; alcune risorse si spostano con un\'azione dedicata',
    observedField: 'Stato osservato — riportato dalla piattaforma, non modificabile'
  }
}
